import React, { useState, useEffect } from "react";
import ReactGA from "react-ga4";
import { Helmet } from "react-helmet";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import Footer from "./Components/Footer";
import NavBar from "./Components/navbar/navbar";
import NavyPanel from "./Components/NavyPanel";
import { ALL_RUN_CLUB_DATA } from './assets/runClubDataIndex';
import MediaCard from "./Components/CardComponent";
import ClubProfile from "./Components/ClubProfile";
import Blogs from "./Components/blogs";
import AboutUs from "./Components/AboutUs";
import BlogPage from "./Components/BlogPage";
import Categories from "./Components/Categories";
import CategoryDetail from "./Components/CategoryDetail.jsx";
import Releases from "./Components/Releases";
import Help from "./Components/Help";
import RegionDetailPage from './pages/RegionDetailPage';
import EventDetail from './pages/EventDetail';
import FeaturedEvent from './Components/FeaturedEvent';
import ClubRedirect from './Components/ClubRedirect.jsx';
import ClubUrlTest from './Components/ClubUrlTest.jsx';
import Terms from './Components/Terms.jsx';
import Privacy from './Components/Privacy.jsx';
import "./App.css";

// Initialize Google Analytics with your Measurement ID
ReactGA.initialize("G-9049ZB29CX");

// This is a local version that's being replaced by the standalone component
// Only kept for backward compatibility
function SEOWrapper({ title, description, keywords, canonicalUrl, children }) {
  // Process canonical URL to ensure consistency
  let finalCanonicalUrl = canonicalUrl;
  if (typeof canonicalUrl === 'function') {
    finalCanonicalUrl = canonicalUrl();
  }
  
  // Ensure consistent domain (www.clubsta.co)
  if (finalCanonicalUrl && finalCanonicalUrl.includes('clubsta.co')) {
    finalCanonicalUrl = finalCanonicalUrl.replace('clubsta.co', 'www.clubsta.co');
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={finalCanonicalUrl} />
      </Helmet>
      {children}
    </>
  );
}

//defines a function that takes the selected city variable and state as arguments

function HomePage({ selectedCity, setSelectedCity }) {
  const [searchTerm, setSearchTerm] = useState('');

  // filter the clubs based on the selected city and search term
  // matches to clubs based on city in the club object 
  const filteredClubs = ALL_RUN_CLUB_DATA.filter((club) => {
    const searchTermLower = searchTerm.trim().toLowerCase();  // Trim search term to remove leading/trailing spaces
    const matchesCity = club.city === selectedCity;
    const matchesSearch = (
      club.title?.toLowerCase().includes(searchTermLower) ||
      club.day?.some(day => day.toLowerCase().includes(searchTermLower)) ||
      club.subtitle?.toLowerCase().includes(searchTermLower)
    );

    return matchesCity && matchesSearch;
  });

  //homepage component returns navy panel, categories, featured event, media card
  //we have it here rather than as a separate child component 
 

  return (
    <div className="main-content">
      <Helmet>
        <title>Discover Local Running Clubs | Clubsta</title>
        <meta name="description" content={`Discover the best run clubs in ${selectedCity || 'your city'}`} />
        <meta name="keywords" content={`run clubs, ${selectedCity || 'your city'}, running clubs, meet people, running groups, best clubs, new city`} />
        {/* Only add homepage canonical when actually on homepage */}
        {window.location.pathname === '/' && (
          <link rel="canonical" href={`https://www.clubsta.co/runclubs/${selectedCity}`} />
        )}
      </Helmet>
      <NavyPanel searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
      <Categories selectedCity={selectedCity} />

      {FeaturedEvent.active && <FeaturedEvent city={selectedCity} />}

      <div className="card-section">
        {filteredClubs.length > 0 ? (
          filteredClubs.map((card) => (
            <MediaCard key={card.id} data={card} />
          ))
        ) : (
          <p>No results found for your search.</p>  // Display 'No results' message
        )}
      </div>
    </div>
  );
}

// app function is basically the top of the tree and everything is loaded beneath
// use react router to render a route -- each element is then loaded when that route is hit

export default function App() {
  //default state set to sf
  const [selectedCity, setSelectedCity] = useState('San Francisco');



  return (
    <Router>
      <div className="app-container">
        <NavBar selectedCity={selectedCity} setSelectedCity={setSelectedCity} />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<HomePage selectedCity={selectedCity} setSelectedCity={setSelectedCity} />} />
            
            {/* Category view route */}
            <Route path="runclubs/:city/category/:categoryName" element={
              <SEOWrapper
                title={`Running clubs in ${selectedCity || 'Your City'} | Clubsta`} 
                description={`Find the best running clubs.`}
                keywords={`run club categories, running groups`}
                canonicalUrl={`https://www.clubsta.co${window.location.pathname}`}
              >
                <CategoryDetail />
              </SEOWrapper>
            } />
            <Route path="/privacy" element={<Privacy />} />
            
            {/* Club profile route */}
            <Route path="runclubs/:city/:id" element={<ClubProfile />} />

            {/* City listing route */}
            <Route path="/runclubs/:city" element={
              <SEOWrapper
                title={`Running Clubs in ${selectedCity || 'Your City'} | Clubsta`}
                description={`Find the best ${selectedCity || 'local'} run clubs and running groups. Browse our curated list of running clubs and join a community of runners today.`}
                keywords={`run club categories, ${selectedCity || 'local'} running, themed running groups, specialized clubs`}
                canonicalUrl={`https://www.clubsta.co${window.location.pathname}`}
              >
                <HomePage selectedCity={selectedCity} setSelectedCity={setSelectedCity} />
              </SEOWrapper>
            } />

            {/* Legacy route for backward compatibility - canonical URL points to preferred URL pattern */}
            <Route path="club/:city/:category/:id" element={
              <SEOWrapper
                title={`Run Club Profile in ${selectedCity || 'Your City'} | Clubsta`}
                description={`Join this running club in ${selectedCity || 'your city'}. Find your perfect running group today.`}
                keywords={`run club profile, ${selectedCity || 'local'} running, running groups`}
                canonicalUrl={(props) => {
                  // Extract parameters from the URL
                  const pathParts = window.location.pathname.split('/');
                  if (pathParts.length >= 4) {
                    const city = pathParts[2];
                    const id = pathParts[4] || pathParts[3]; // Handle both with and without category
                    // Return canonical URL with the preferred URL pattern
                    return `https://www.clubsta.co/runclubs/${city}/${id}`;
                  }
                  return `https://www.clubsta.co${window.location.pathname}`;
                }}
              >
                <ClubProfile />
              </SEOWrapper>
            } />

            {/* Other routes */}


            <Route path="/blogs" element={
              <SEOWrapper
                title="Running Blogs and Articles | Clubsta"
                description="Read the latest running blogs and articles from Clubsta. Get tips, inspiration, and stay updated on the running scene."
                keywords="running blogs, running articles, running tips"
                canonicalUrl={`https://www.clubsta.co${window.location.pathname}`}
              >
                <Blogs />
              </SEOWrapper>
            } />
            {/*Terms and conditions */}
            <Route path="/terms" element={
                <Terms />
            } />
            <Route path="/blogs/:id" element={
              <SEOWrapper
                title="Blog Post | Clubsta"
                description="Read this in-depth blog post about running. Get valuable insights and tips from the Clubsta community."
                keywords="running blog post, running tips, Clubsta article"
                canonicalUrl={`https://www.clubsta.co${window.location.pathname}`}
              >
                <BlogPage />
              </SEOWrapper>
            } />
            <Route path="/aboutus" element={
              <SEOWrapper
                title="About Clubsta | Your Run Club Directory"
                description="Learn about Clubsta, your go-to directory for run clubs. Discover our mission to connect runners and build communities."
                keywords="about Clubsta, run clubs, running community, club directory"
                canonicalUrl={`https://www.clubsta.co${window.location.pathname}`}
              >
                <AboutUs />
              </SEOWrapper>
            } />
            <Route path="/releases" element={
              <SEOWrapper
                title="Clubsta Release Notes and Updates"
                description="Stay up-to-date with the latest features and improvements to Clubsta. See what's new in our run club directory."
                keywords="Clubsta updates, release notes, new features"
                canonicalUrl={`https://www.clubsta.co${window.location.pathname}`}
              >
                <Releases />
              </SEOWrapper>
            } />
            <Route path="/help" element={
              <SEOWrapper
                title="Help and Support - Clubsta"
                description="Get help using Clubsta. Find answers to common questions about our run club directory and how to get the most out of it."
                keywords="Clubsta help, support, FAQ, run club directory"
                canonicalUrl={`https://www.clubsta.co${window.location.pathname}`}
              >
                <Help />
              </SEOWrapper>
            } />
            <Route path="runclub/region/:region" element={
              <SEOWrapper
                title="Running Clubs by Region - Find Local Running Groups | Clubsta"
                description="Discover running clubs and groups in your area. Join local running communities and find the perfect running club for you."
                keywords="running clubs, running groups, running communities, local running clubs"
                canonicalUrl={`https://www.clubsta.co${window.location.pathname}`}
              >
                <RegionDetailPage />
              </SEOWrapper>
            } />
            
            {/* Event detail route */}
            <Route path="events/:city/:eventId" element={
              <SEOWrapper
                title={`Running Event in ${selectedCity || 'Your City'} | Clubsta`}
                description={`Join this special running event in ${selectedCity || 'your city'}. Discover unique running experiences and connect with local runners.`}
                keywords={`running event, ${selectedCity || 'local'} running, running community, special events`}
                canonicalUrl={`https://www.clubsta.co${window.location.pathname}`}
              >
                <EventDetail city={selectedCity} />
              </SEOWrapper>
            } />
            
            {/* Test route for viewing all short URLs - remove in production */}
            <Route path="/test-club-urls" element={<ClubUrlTest />} />
            
            {/* Short club URL redirect routes - must be last before catch-all */}
            <Route path="/:clubSlug/:citySlug/:number" element={<ClubRedirect />} />
            <Route path="/:clubSlug/:citySlug" element={<ClubRedirect />} />
            <Route path="/:shortSlug" element={<ClubRedirect />} />
            <Route path="*" element={<HomePage selectedCity={selectedCity} setSelectedCity={setSelectedCity} />} />
          </Routes>
        </main>
        <Footer setSelectedCity={setSelectedCity} />
      </div>
    </Router>
  );
}
